/* Local selected Concept A renderer. Generated by build.mjs. */
(()=>{
'use strict';
const FORMATS = new Set(['cushion', 'piece', 'throw', 'fitted', 'chair', 'mixed', 'unknown']);
const DIAGRAMS = new Set(['cushion', 'panel', 'throw', 'sofa', 'chair', 'pillowcase', 'none']);
const STATUSES = new Set(['chart', 'listed', 'unconfirmed', 'conflict', 'label-only']);
const text = value => typeof value === 'string' ? value.trim() : '';
const escapeHTML = value => String(value ?? '').replace(/[&<>"']/g, character => ({'&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;'}[character]));
const safeJSON = value => JSON.stringify(value).replace(/</g, '\\u003c').replace(/\u2028/g, '\\u2028').replace(/\u2029/g, '\\u2029');
const numericID = value => /^\d+$/.test(String(value ?? '')) ? String(value) : null;

// Deliberately whitelist display fields. Source options, evidence and gaps never
// leave the internal profile, and native variants are represented by IDs only.
function sanitizeProfile(source) {
  if (!source || !numericID(source.id)) throw new Error('A numeric product ID is required.');
  const profile = {
    id: String(source.id), brand: text(source.brand), heading: text(source.heading) || 'Explore the listed sizes',
    format: FORMATS.has(source.format) ? source.format : 'unknown',
    groups: (Array.isArray(source.groups) ? source.groups : []).map((group, groupIndex) => ({
      id: `g${groupIndex}`, label: text(group.label) || 'Sizes',
      diagram: DIAGRAMS.has(group.diagram) ? group.diagram : 'none', measureNote: text(group.measureNote),
      rows: (Array.isArray(group.rows) ? group.rows : []).map((row, rowIndex) => {
        const status = STATUSES.has(row.status) ? row.status : 'unconfirmed';
        return {
          key: `g${groupIndex}-r${rowIndex}`, label: text(row.label), status, note: text(row.note),
          dimensions: ['chart', 'listed'].includes(status) && Array.isArray(row.dimensions)
            ? row.dimensions.map(dimension => ({label:text(dimension.label), value:text(dimension.value), unit:text(dimension.unit)})).filter(dimension => dimension.label && dimension.value)
            : []
        };
      }).filter(row => row.label)
    })),
    caution: text(source.caution), details: text(source.details), emptyState: text(source.emptyState)
  };
  if (!profile.groups.length) profile.groups.push({id:'g0', label:'Size information', diagram:'none', measureNote:'', rows:[]});
  return profile;
}

function normalizeVariantRows(source, profile) {
  const keys = new Set(profile.groups.flatMap(group => group.rows.map(row => row.key)));
  return Object.fromEntries(Object.entries(source || {}).filter(([id, key]) => numericID(id) && keys.has(key)));
}

function findSelection(profile, key) {
  for (let groupIndex=0; groupIndex<profile.groups.length; groupIndex++) {
    const rowIndex = profile.groups[groupIndex].rows.findIndex(row => row.key === key);
    if (rowIndex !== -1) return {groupIndex, rowIndex};
  }
  return {groupIndex:0, rowIndex:profile.groups[0].rows.length ? 0 : -1};
}

function icon(diagram) {
  const paths = {
    cushion:'<rect x="4" y="7" width="16" height="10" rx="3"/><path d="M6 14h12M6 17v2m12-2v2"/>',
    panel:'<path d="m5 4 14 2 1 14-15-2Z"/><path d="m8 7 8 1m-8 2 8 1M7 15l9 1"/>',
    throw:'<path d="m5 4 14 2 1 13q-4 2-8 0t-7 0Z"/><path d="m7 7 9 1M7 10l9 1"/>',
    sofa:'<path d="M5 11V7a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v4M5 18v2m14-2v2"/><path d="M3 11h3v4h12v-4h3v7H3Z"/>',
    chair:'<rect x="7" y="3" width="10" height="11" rx="2"/><path d="M5 12v6h14v-6M7 18v3m10-3v3M7 15h10"/>',
    pillowcase:'<rect x="4" y="4" width="16" height="16" rx="4"/><path d="m7 7 10 10M7 17 17 7"/>',
    none:'<path d="M4 7h16v10H4Z"/><path d="M7 7v4m4-4v3m4-3v4m3-4v3"/>'
  };
  return `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">${paths[diagram] || paths.none}</svg>`;
}

function axisArrows(row, diagram) {
  if (!row || !['cushion', 'panel', 'throw', 'pillowcase'].includes(diagram)) return '';
  const first = row.dimensions.find(dimension => /^(?:listed )?side 1$|^first side$|^length$/i.test(dimension.label));
  const second = row.dimensions.find(dimension => /^(?:listed )?side 2$|^second side$|^width$/i.test(dimension.label));
  const third = diagram === 'cushion' ? row.dimensions.find(dimension => /^thickness$/i.test(dimension.label)) : null;
  const value = dimension => escapeHTML([dimension.value,dimension.unit].filter(Boolean).join(' '));
  let markup = '';
  if (first) markup += `<path d="M117 117v9m173-9v9m-173-4h173m-173 0 5-3m-5 3 5 3m168-3-5-3m5 3-5 3" stroke="#746c5e" fill="none" stroke-width="1"/><text x="204" y="144" text-anchor="middle" class="sd-a-axis">${escapeHTML(first.label)} <tspan>${value(first)}</tspan></text>`;
  if (second) markup += `<path d="M96 36 74 77m-2-3 3 5 5-1m12-40 5-4 2 5" stroke="#746c5e" fill="none" stroke-width="1"/><text x="15" y="25" class="sd-a-axis">${escapeHTML(second.label)}</text><text x="15" y="40" class="sd-a-axis-value">${value(second)}</text>`;
  if (third) markup += `<path d="M307 83h11m-11 23h11m-6-23v23m-3-20 3-3 3 3m-6 17 3 3 3-3" stroke="#746c5e" fill="none" stroke-width="1"/><text x="327" y="89" class="sd-a-axis">${escapeHTML(third.label)}</text><text x="327" y="105" class="sd-a-axis-value">${value(third)}</text>`;
  return markup;
}

function diagramMarkup(diagram, row, prefix) {
  const top = `${prefix}-top`, edge = `${prefix}-edge`;
  const shapes = {
    cushion:`<ellipse cx="208" cy="108" rx="115" ry="11" fill="#29251f" opacity=".045"/><path d="M110 76 Q103 79 105 88 L114 101 Q116 106 129 107 L274 107 Q287 106 291 98 L298 87 L297 75Z" fill="url(#${edge})" stroke="#afa48f" stroke-width="1.2"/><path d="M134 35 Q137 30 149 30 H251 Q266 30 272 36 L298 73 Q303 83 285 86 H119 Q102 85 109 75Z" fill="url(#${top})" stroke="#b8ae9a" stroke-width="1.2"/><path d="M142 38 Q145 36 152 36 H249 Q260 36 264 40 L288 73 Q290 77 282 78 H123 Q116 78 118 74Z" fill="none" stroke="#c9c0af" stroke-width=".8" stroke-dasharray="2 3"/>`,
    panel:`<ellipse cx="208" cy="108" rx="110" ry="8" fill="#29251f" opacity=".045"/><path d="m133 33 138 6 27 59-186-7Z" fill="url(#${top})" stroke="#b8ae9a" stroke-width="1.2"/><path d="m139 40 126 5 23 45-168-6Z" fill="none" stroke="#c9c0af" stroke-width=".8" stroke-dasharray="2 3"/>`,
    throw:`<ellipse cx="208" cy="108" rx="110" ry="8" fill="#29251f" opacity=".045"/><path d="m135 32 134 6 29 59q-12 6-23 0t-23 0-23 0-23 0-23 0-23 0-23 0-25 0Z" fill="url(#${top})" stroke="#b8ae9a" stroke-width="1.2"/><path d="m141 40 122 5 18 40m-131-35 16 27m102-27 17 38" fill="none" stroke="#b8ae9a" stroke-width="1"/>`,
    sofa:`<ellipse cx="210" cy="120" rx="122" ry="9" fill="#29251f" opacity=".045"/><path d="M111 73V44q0-13 14-13h168q14 0 14 13v29" fill="url(#${top})" stroke="#b8ae9a" stroke-width="1.3"/><path d="M113 73q30-10 94 0v22h-94Zm98 0q60-10 94 0v22h-94Z" fill="url(#${top})" stroke="#b8ae9a" stroke-width="1.3"/><path d="M98 65h17v32h190V65h17v48H98Z" fill="url(#${edge})" stroke="#afa48f" stroke-width="1.3"/><path d="M113 113v10m194-10v10" stroke="#8e826c" stroke-width="4"/>`,
    chair:`<ellipse cx="210" cy="126" rx="66" ry="7" fill="#29251f" opacity=".045"/><rect x="162" y="20" width="96" height="69" rx="12" fill="url(#${top})" stroke="#b8ae9a" stroke-width="1.3"/><path d="M160 82q50-12 100 0v19H160Z" fill="url(#${top})" stroke="#b8ae9a" stroke-width="1.3"/><path d="M147 70h15v32h96V70h15v43H147Z" fill="url(#${edge})" stroke="#afa48f" stroke-width="1.3"/><path d="M160 113v15m100-15v15" stroke="#8e826c" stroke-width="4"/>`,
    pillowcase:`<ellipse cx="209" cy="108" rx="101" ry="8" fill="#29251f" opacity=".045"/><path d="M135 32q70 10 140 0-10 35 13 68-79-10-161 0 15-37 8-68Z" fill="url(#${top})" stroke="#b8ae9a" stroke-width="1.3"/><path d="M143 40q62 8 124 0-7 28 10 52-68-8-138 0 10-29 4-52Z" fill="none" stroke="#c9c0af" stroke-width=".8" stroke-dasharray="2 3"/>`,
    none:`<rect x="150" y="42" width="120" height="58" rx="9" fill="url(#${top})" stroke="#b8ae9a" stroke-width="1.3"/><path d="M168 43v23m18-23v15m18-15v23m18-23v15m18-15v23m18-23v15" stroke="#9d927e" stroke-width="1.4"/>`
  };
  const description = `${row ? `${row.label}. ` : ''}Illustrative ${diagram === 'none' ? 'measuring tool' : diagram}; not to scale. ${row?.dimensions.map(dimension => `${dimension.label}: ${dimension.value} ${dimension.unit}`.trim()).join('. ') || 'No confirmed dimensions are drawn.'}`;
  return `<svg class="sd-a-diagram" viewBox="0 0 420 152" role="img" aria-labelledby="${prefix}-diagram-title ${prefix}-diagram-desc"><title id="${prefix}-diagram-title">${escapeHTML(diagram === 'none' ? 'Measurement information' : `${diagram.charAt(0).toUpperCase()+diagram.slice(1)} illustration`)}</title><desc id="${prefix}-diagram-desc">${escapeHTML(description)}</desc><defs><linearGradient id="${top}" x1="0" y1="0" x2="0.8" y2="1"><stop stop-color="#faf8f3"/><stop offset="1" stop-color="#e5dfd3"/></linearGradient><linearGradient id="${edge}" x1="0" y1="0" x2="0" y2="1"><stop stop-color="#ded6c6"/><stop offset="1" stop-color="#cfc4b1"/></linearGradient></defs>${shapes[diagram] || shapes.none}${axisArrows(row,diagram)}</svg>`;
}

function renderPanel(profile, selection, prefix) {
  const group = profile.groups[selection.groupIndex], row = group.rows[selection.rowIndex] || null;
  const dimensions = row?.dimensions || [];
  const heading = row?.status === 'chart' ? 'Chart measurements' : dimensions.length ? 'Listed dimensions' : row?.status === 'conflict' ? 'Details need confirmation' : 'Listed options';
  const longest = Math.max(0,...group.rows.map(item => item.label.length));
  // Keep compound measurements and their units/quantity together. These labels
  // need more space than their character count alone suggests at mobile widths.
  const compoundMeasurement = group.rows.some(item => /\d[^\s]*["″][^\s]*\/[^\s]*\d|\d[^\s]*(?:cm|mm|in)-\d+pcs?\b|\bseater\s+\d+(?:\.\d+)?[-–]\d+(?:\.\d+)?\s*(?:cm|mm|in)\b/i.test(item.label));
  const chipSize = longest > 22 || compoundMeasurement ? 'long' : longest > 6 ? 'medium' : 'short';
  const choiceMarkup = group.rows.length ? `<div class="sd-a-choice-heading"><span>${escapeHTML(group.label)}${/size|option/i.test(group.label) ? '' : ' sizes'}</span><span>${escapeHTML(heading)}</span></div><div class="sd-a-choices" role="group" aria-label="Explore ${escapeHTML(group.label)} sizes" data-size-choices data-label-length="${chipSize}" data-single="${group.rows.length === 1}">${group.rows.map(item => `<button type="button" aria-pressed="${item.key === row?.key}" data-size-key="${item.key}">${escapeHTML(item.label)}</button>`).join('')}</div>` : '';
  const unavailable = row?.status === 'conflict' ? 'Measurements need confirmation' : row?.status === 'label-only' ? 'No numeric fit range supplied' : 'Dimensions not confirmed';
  return `${choiceMarkup}<div class="sd-a-preview"><div class="sd-a-selected"><strong data-selected-label>${escapeHTML(row?.label || profile.emptyState || 'No size selection listed')}</strong><span>Illustration · not to scale</span></div>${diagramMarkup(group.diagram, row, prefix)}${dimensions.length ? `<dl class="sd-a-readout" data-count="${Math.min(dimensions.length,4)}">${dimensions.map(dimension => `<div><dt>${escapeHTML(dimension.label)}</dt><dd><span>${escapeHTML(dimension.value)}</span>${dimension.unit ? `<small> ${escapeHTML(dimension.unit)}</small>` : ''}</dd></div>`).join('')}</dl>` : `<div class="sd-a-unconfirmed"><strong>${escapeHTML(unavailable)}</strong><a href="/pages/contact">Ask about this size <span aria-hidden="true">↗</span></a></div>`}</div>${group.measureNote ? `<p class="sd-a-type-note" data-type-note>${escapeHTML(group.measureNote)}</p>` : ''}${row?.note ? `<p class="sd-a-row-note" data-row-note>${escapeHTML(row.note)}</p>` : ''}`;
}

function renderContent(profile, selection, prefix) {
  const group = profile.groups[selection.groupIndex];
  return `<header class="sd-a-header"><span class="sd-a-mark" aria-hidden="true">${icon(group.diagram)}</span><div>${profile.brand ? `<p class="sd-a-brand">${escapeHTML(profile.brand)}</p>` : ''}<h2 id="${prefix}-title">${escapeHTML(profile.heading)}</h2></div></header>${profile.groups.length > 1 ? `<div class="sd-a-tabs" role="tablist" aria-label="Piece or size group" data-count="${Math.min(profile.groups.length,4)}">${profile.groups.map((item,index) => `<button type="button" role="tab" id="${prefix}-tab-${index}" aria-controls="${prefix}-panel" aria-selected="${index === selection.groupIndex}" tabindex="${index === selection.groupIndex ? 0 : -1}" data-group-index="${index}">${icon(item.diagram)}<span>${escapeHTML(item.label)}</span></button>`).join('')}</div>` : ''}<div class="sd-a-panel" id="${prefix}-panel" ${profile.groups.length > 1 ? `role="tabpanel" aria-labelledby="${prefix}-tab-${selection.groupIndex}"` : `aria-label="${escapeHTML(group.label)}"`} data-size-panel>${renderPanel(profile,selection,prefix)}</div><footer class="sd-a-footer">${profile.caution ? `<p class="sd-a-caution"><svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><circle cx="8" cy="8" r="6.2"/><path d="M8 7v4m0-7v1"/></svg><span>${escapeHTML(profile.caution)}</span></p>` : ''}<p class="sd-a-purchase-note">Browse sizes here; choose purchase options above.</p>${profile.details ? `<details><summary>About these measurements <span aria-hidden="true">+</span></summary><div>${escapeHTML(profile.details)}</div></details>` : ''}</footer><p class="sd-a-live" role="status" aria-live="polite" aria-atomic="true" data-size-announcement></p><noscript><p class="sd-a-type-note">Interactive size browsing needs JavaScript. Check all listed options and the product size information above, or <a href="/pages/contact">ask us about measurements</a>.</p></noscript>`;
}

function renderWidget(source, {variantRows={},initialVariantId=null,instanceId=null}={}) {
  const profile = sanitizeProfile(source), mapping = normalizeVariantRows(variantRows,profile);
  const variant = numericID(initialVariantId);
  const prefix = (instanceId || `sd-size-${profile.id}`).replace(/[^a-zA-Z0-9_-]/g,'-');
  const selection = findSelection(profile,mapping[variant]);
  const data = {profile,variantRows:mapping,initialVariantId:variant};
  return `<sd-size-browser class="sd-size-browser" id="${prefix}" data-product-id="${profile.id}" aria-labelledby="${prefix}-title">${renderContent(profile,selection,prefix)}<script type="application/json" data-sd-size-data>${safeJSON(data)}<\/script></sd-size-browser>`;
}

if (typeof window !== 'undefined' && window.customElements && !window.customElements.get('sd-size-browser')) {
  let instanceCounter = 0;
  class SDSizeBrowser extends HTMLElement {
    connectedCallback() {
      if (this._abort) return;
      try {
        if (!this._data) {
          const source = JSON.parse(this.querySelector('[data-sd-size-data]')?.textContent || '{}');
          const profile = sanitizeProfile(source.profile);
          this._data = {profile, variantRows:normalizeVariantRows(source.variantRows,profile), initialVariantId:numericID(source.initialVariantId)};
        }
        this._abort = new AbortController();
        this._timers = new Set();
        this._prefix = `sd-size-${this._data.profile.id}-instance-${++instanceCounter}`;
        this.id = this._prefix;
        this.setAttribute('aria-labelledby',`${this._prefix}-title`);
        this._selection = this._selection || findSelection(this._data.profile,this._data.variantRows[this._data.initialVariantId]);
        this._remembered = this._remembered || new Map();
        this._lastNativeVariant = null;
        this.innerHTML = renderContent(this._data.profile,this._selection,this._prefix);
        const dataNode = document.createElement('script');
        dataNode.type = 'application/json';
        dataNode.dataset.sdSizeData = '';
        dataNode.textContent = safeJSON(this._data);
        this.append(dataNode);
        this._updateDataset();
        this.dataset.initialized = 'true';
        const signal = this._abort.signal;
        this.addEventListener('click',event => this._click(event),{signal});
        this.addEventListener('keydown',event => this._key(event),{signal});
        const schedule = event => {
          if (this.contains(event.target)) return;
          const container = event.target?.closest?.('product-form-component,variant-picker,form');
          if (container && (container.dataset.productId === this._data.profile.id || this._section?.contains(container))) this._scheduleNativeRead();
        };
        document.addEventListener('change',schedule,{signal,capture:true});
        document.addEventListener('input',schedule,{signal,capture:true});
        for (const eventName of ['variant:update','variant:change','variant:changed','product:variant-change','variant-change']) {
          document.addEventListener(eventName,event => this._variantEvent(event),{signal});
        }
        this._section = this.closest('[id^="shopify-section-"],.shopify-section') || this.parentElement;
        if (this._section) {
          this._observer = new MutationObserver(mutations => {
            if (mutations.some(mutation => !this.contains(mutation.target))) this._scheduleNativeRead();
          });
          this._observer.observe(this._section,{subtree:true,childList:true,attributes:true,attributeFilter:['value']});
        }
        this._readNativeVariant(false);
      } catch (error) {
        // Keep the fully rendered server fallback if initialization fails.
        this.dataset.initializationError = 'true';
        console.error('Size browser could not initialize.',error);
      }
    }

    disconnectedCallback() {
      this._abort?.abort();
      this._abort = null;
      this._observer?.disconnect();
      this._observer = null;
      this._timers?.forEach(timer => clearTimeout(timer));
      this._timers?.clear();
    }

    _updateDataset() {
      const group = this._data.profile.groups[this._selection.groupIndex];
      const row = group.rows[this._selection.rowIndex];
      this.dataset.selectedGroup = group.id;
      this.dataset.selectedSize = row?.label || '';
      this.dataset.selectedRow = row?.key || '';
      this.dataset.measurementStatus = row?.status || 'unconfirmed';
    }

    _select(selection,{focus=null,announce=true}={}) {
      const group = this._data.profile.groups[selection.groupIndex];
      if (!group || (selection.rowIndex >= 0 && !group.rows[selection.rowIndex])) return;
      this._selection = selection;
      this._remembered.set(selection.groupIndex,selection.rowIndex);
      this.querySelectorAll('[data-group-index]').forEach(button => {
        const selected = Number(button.dataset.groupIndex) === selection.groupIndex;
        button.setAttribute('aria-selected',String(selected));
        button.tabIndex = selected ? 0 : -1;
      });
      const panel = this.querySelector('[data-size-panel]');
      if (this._data.profile.groups.length > 1) panel.setAttribute('aria-labelledby',`${this._prefix}-tab-${selection.groupIndex}`);
      panel.innerHTML = renderPanel(this._data.profile,selection,this._prefix);
      this.querySelector('.sd-a-mark').innerHTML = icon(group.diagram);
      this._updateDataset();
      if (focus === 'group') this.querySelector(`[data-group-index="${selection.groupIndex}"]`)?.focus({preventScroll:true});
      if (focus === 'row') this.querySelector(`[data-size-key="${group.rows[selection.rowIndex]?.key}"]`)?.focus({preventScroll:true});
      if (announce) {
        const row = group.rows[selection.rowIndex];
        const measurements = row?.dimensions.map(dimension => `${dimension.label}: ${dimension.value} ${dimension.unit}`.trim()).join('. ');
        this.querySelector('[data-size-announcement]').textContent = `Size guide: ${group.label}${row ? `, ${row.label}` : ''}. ${measurements || (row?.status === 'conflict' ? 'Measurements need confirmation.' : 'No confirmed numeric measurements are shown.')}`;
      }
    }

    _click(event) {
      const button = event.target.closest('button');
      if (!button || !this.contains(button)) return;
      if (button.hasAttribute('data-size-key')) {
        this._select(findSelection(this._data.profile,button.dataset.sizeKey),{focus:'row'});
      } else if (button.hasAttribute('data-group-index')) {
        const groupIndex = Number(button.dataset.groupIndex);
        const group = this._data.profile.groups[groupIndex];
        this._select({groupIndex,rowIndex:this._remembered.get(groupIndex) ?? (group.rows.length ? 0 : -1)},{focus:'group'});
      }
    }

    _key(event) {
      const button = event.target.closest('[data-group-index]');
      if (!button || !this.contains(button)) return;
      const count = this._data.profile.groups.length;
      const current = Number(button.dataset.groupIndex);
      let groupIndex;
      if (event.key === 'ArrowRight') groupIndex = (current+1)%count;
      else if (event.key === 'ArrowLeft') groupIndex = (current+count-1)%count;
      else if (event.key === 'Home') groupIndex = 0;
      else if (event.key === 'End') groupIndex = count-1;
      else return;
      event.preventDefault();
      const group = this._data.profile.groups[groupIndex];
      this._select({groupIndex,rowIndex:this._remembered.get(groupIndex) ?? (group.rows.length ? 0 : -1)},{focus:'group'});
    }

    _variantEvent(event) {
      const detail = event.detail || {};
      const productId = detail.data?.productId || detail.productId || detail.product?.id;
      if (productId && String(productId).replace(/^.*\//,'') !== this._data.profile.id) return;
      const variantId = numericID(detail.resource?.id || detail.variant?.id || detail.variantId || detail.id);
      if (variantId && this._data.variantRows[variantId]) {
        this._syncVariant(variantId,true);
      } else {
        this._scheduleNativeRead();
      }
    }

    _forms() {
      const scope = this._section || document;
      // HTMLFormElement's `id` property can be shadowed by its input[name=id].
      // Read attributes explicitly and never use the payment-terms form as the
      // selected purchase variant: it can retain the initial variant value.
      const forms = Array.from(scope.querySelectorAll('form[action*="/cart/add"]')).filter(form =>
        !/installment/i.test(form.getAttribute('id') || '') && !form.classList.contains('payment-terms')
      );
      const belongsToProduct = form => form.closest('product-form-component')?.getAttribute('data-product-id') === this._data.profile.id || form.querySelector('[name="product-id"]')?.value === this._data.profile.id;
      const purchase = forms.filter(form => form.getAttribute('data-type') === 'add-to-cart-form');
      const matchingPurchase = purchase.filter(belongsToProduct);
      if (matchingPurchase.length) return matchingPurchase;
      const mappedPurchase = purchase.filter(form => Boolean(this._data.variantRows[form.querySelector('[name="id"]')?.value]));
      if (mappedPurchase.length) return mappedPurchase;
      return forms.filter(belongsToProduct);
    }

    _readNativeVariant(announce=true) {
      for (const form of this._forms()) {
        const variantId = numericID(form.querySelector('[name="id"]')?.value);
        if (variantId && this._data.variantRows[variantId]) {
          this._syncVariant(variantId,announce);
          return;
        }
      }
    }

    _syncVariant(variantId,announce) {
      if (variantId === this._lastNativeVariant) return;
      this._lastNativeVariant = variantId;
      this.dataset.nativeVariantId = variantId;
      const key = this._data.variantRows[variantId];
      if (key && key !== this.dataset.selectedRow) this._select(findSelection(this._data.profile,key),{announce});
    }

    _scheduleNativeRead() {
      this._timers.forEach(timer => clearTimeout(timer));
      this._timers.clear();
      [0,120,450,1000].forEach(delay => {
        const timer = setTimeout(() => {this._timers.delete(timer);if(this.isConnected)this._readNativeVariant();},delay);
        this._timers.add(timer);
      });
    }
  }
  window.customElements.define('sd-size-browser',SDSizeBrowser);
}

})();
